import type { UserDetails } from '../models/UserDetails';
import { decodeToken } from '../utils/constants';
import { getToken } from './LocalStorageApi';

export const getAuthHeader = async (): Promise<[Headers, UserDetails]> => {
  const token = getToken();
  if (!token) {
    throw new Error('Please log in.');
  }
  const details = decodeToken(token);
  if (!details) {
    throw new Error('Invalid token. Please log in again.');
  }
  if (details.exp < Date.now() / 1000) {
    throw new Error('Session expired. Please log in again.');
  }
  return [
    new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token ?? ''}`,
    }),
    details,
  ];
};

type ErrorWithMessage = {
  message: string;
};

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) return maybeError;

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback in case there's an error stringifying the maybeError
    // like with circular references for example.
    return new Error(String(maybeError));
  }
}

export const getErrorMessage = (error: unknown) => {
  return toErrorWithMessage(error).message;
};

export const handleError = (error: unknown) => {
  const message = getErrorMessage(error);

  return message;
};
